import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getProject, updateProject, deleteProject, getAllUsers, archiveResource, exportProject, exportProjectPDF, addProjectAttachment, deleteProjectAttachment } from '@/services/api';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { toast } from 'sonner';
import {
  ArrowLeft, Trash2, Users, Target, CheckCircle2, Circle,
  Save, UserPlus, X, Archive, Download, Mail, FileText,
  FileSpreadsheet, Paperclip, Upload
} from 'lucide-react';
import { phases, getPhaseProgress } from './projectConstants';
import { phaseBarCls } from '@/utils/styleHelpers';

export const ProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isMasterAdmin, user } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activePhase, setActivePhase] = useState('define');
  const [allUsers, setAllUsers] = useState([]);
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [exporting, setExporting] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const attachmentInputRef = useRef(null);

  useEffect(() => {
    loadProject();
    if (isMasterAdmin) {
      loadUsers();
    }
  }, [id, isMasterAdmin]);

  const loadProject = async () => {
    try {
      const data = await getProject(id);
      setProject(data);
    } catch (error) {
      toast.error('Failed to load project');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const users = await getAllUsers();
      setAllUsers(users);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const handlePhaseUpdate = async (phaseKey, updates) => {
    setSaving(true);
    try {
      const updatedPhases = {
        ...project.phases,
        [phaseKey]: { ...project.phases[phaseKey], ...updates }
      };
      await updateProject(id, { phases: updatedPhases });
      setProject({ ...project, phases: updatedPhases });
      toast.success('Phase updated');
    } catch (error) {
      toast.error('Failed to update phase');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!window.confirm('Archive this project? It can be restored from the Archive page.')) return;
    try {
      await archiveResource('project', id);
      toast.success('Project archived');
      navigate('/projects');
    } catch (error) {
      toast.error('Failed to archive project');
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      let data;
      if (format === 'pdf') {
        data = await exportProjectPDF(id);
      } else {
        data = await exportProject(id, format);
      }
      
      if (data instanceof Blob) {
        const url = window.URL.createObjectURL(data);
        const a = document.createElement('a');
        a.href = url;
        const extMap = { excel: 'xlsx', pdf: 'pdf', docx: 'docx', json: 'json' };
        const ext = extMap[format] || format;
        a.download = `project-${project.title.replace(/\s+/g, '_')}.${ext}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `project-${project.title.replace(/\s+/g, '_')}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
      
      toast.success(`Exported as ${format.toUpperCase()}`);
      setShowExportDialog(false);
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleEmailShare = (format) => {
    const subject = encodeURIComponent(`KAIZEN KANRI - Project: ${project.title}`);
    const body = encodeURIComponent(`Please find attached the ${format.toUpperCase()} export of the project "${project.title}".\n\nProject Details:\n- Industry: ${project.industry}\n- Status: ${project.status}\n- Goal: ${project.goal}`);
    window.open(`mailto:${emailTo}?subject=${subject}&body=${body}`, '_blank');
    setShowEmailDialog(false);
    toast.info('Email client opened. Please attach the exported file manually.');
  };

  const handleAttachmentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }
    setUploadingAttachment(true);
    try {
      const result = await addProjectAttachment(id, file);
      const newAttachment = { ...result, filename: file.name };
      setProject({
        ...project,
        attachments: [...(project.attachments || []), newAttachment]
      });
      toast.success('Attachment added');
    } catch (error) {
      toast.error(error.message || 'Failed to upload attachment');
    } finally {
      setUploadingAttachment(false);
      if (attachmentInputRef.current) attachmentInputRef.current.value = '';
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      await deleteProjectAttachment(id, attachmentId);
      setProject({
        ...project,
        attachments: project.attachments.filter(a => a.attachment_id !== attachmentId)
      });
      toast.success('Attachment removed');
    } catch (error) {
      toast.error('Failed to remove attachment');
    }
  };

  const handleAddParticipant = async () => {
    if (!selectedUser) return;
    const currentMembers = project.team_members || [];
    if (currentMembers.includes(selectedUser)) {
      toast.error('User is already a participant');
      return;
    }
    setSaving(true);
    try {
      const updatedMembers = [...currentMembers, selectedUser];
      await updateProject(id, { team_members: updatedMembers });
      setProject({ ...project, team_members: updatedMembers });
      setSelectedUser('');
      setShowAddParticipant(false);
      toast.success('Participant added');
    } catch (error) {
      toast.error('Failed to add participant');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveParticipant = async (userId) => {
    setSaving(true);
    try {
      const updatedMembers = (project.team_members || []).filter(m => m !== userId);
      await updateProject(id, { team_members: updatedMembers });
      setProject({ ...project, team_members: updatedMembers });
      toast.success('Participant removed');
    } catch (error) {
      toast.error('Failed to remove participant');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await deleteProject(id);
      toast.success('Project deleted');
      navigate('/projects');
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const canEdit = isAdmin || project?.created_by === user?.user_id;
  
  const getParticipantInfo = (userId) => {
    const userInfo = allUsers.find(u => u.user_id === userId);
    return userInfo || { name: userId, email: '' };
  };

  const availableUsers = allUsers.filter(u => 
    !(project?.team_members || []).includes(u.user_id) && 
    u.user_id !== project?.created_by
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!project) return null;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="project-detail-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/projects')} data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge className="capitalize">{project.industry}</Badge>
                <Badge variant={project.status === 'active' ? 'default' : 'secondary'}>{project.status}</Badge>
              </div>
              <h1 className="text-2xl font-bold tracking-tight">{project.title}</h1>
              <p className="text-muted-foreground font-body mt-1">{project.description}</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" /> Export
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Export Project</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-3 pt-4">
                  <Button variant="outline" onClick={() => handleExport('json')} disabled={exporting}>
                    <FileText className="h-5 w-5 mr-2" /> JSON
                  </Button>
                  <Button variant="outline" onClick={() => handleExport('excel')} disabled={exporting}>
                    <FileSpreadsheet className="h-5 w-5 mr-2 text-green-600" /> Excel
                  </Button>
                  <Button variant="outline" onClick={() => handleExport('docx')} disabled={exporting}>
                    <FileText className="h-5 w-5 mr-2 text-blue-600" /> Word
                  </Button>
                  <Button variant="outline" onClick={() => handleExport('pdf')} disabled={exporting}>
                    <FileText className="h-5 w-5 mr-2 text-red-600" /> PDF
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Mail className="h-4 w-4 mr-2" /> Share
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Share via Email</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div>
                    <Label>Email Address</Label>
                    <Input
                      type="email"
                      placeholder="Enter email or select a user"
                      value={emailTo}
                      onChange={(e) => setEmailTo(e.target.value)}
                    />
                  </div>
                  {allUsers.length > 0 && (
                    <div>
                      <Label>Or Select User</Label>
                      <Select onValueChange={(v) => setEmailTo(v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a user..." />
                        </SelectTrigger>
                        <SelectContent>
                          {allUsers.filter(u => u.email).map(u => (
                            <SelectItem key={u.user_id} value={u.email}>
                              {u.name} ({u.email})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button className="flex-1" onClick={() => handleEmailShare('excel')} disabled={!emailTo}>
                      <Mail className="h-4 w-4 mr-2" /> Send Excel
                    </Button>
                    <Button variant="outline" className="flex-1" onClick={() => handleEmailShare('docx')} disabled={!emailTo}>
                      <Mail className="h-4 w-4 mr-2" /> Send Word
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Button variant="outline" size="sm" onClick={handleArchive}>
              <Archive className="h-4 w-4 mr-2" /> Archive
            </Button>

            {isAdmin && (
              <Button variant="destructive" size="sm" onClick={handleDelete} data-testid="delete-project-btn">
                <Trash2 className="h-4 w-4 mr-2" /> Delete
              </Button>
            )}
          </div>
        </div>

        {/* Progress */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Overall Progress</h3>
              <span className="text-2xl font-bold">{Math.round(getPhaseProgress(project.phases))}%</span>
            </div>
            <div className="flex gap-2">
              {phases.map((phase) => {
                const status = project.phases?.[phase.key]?.status || 'not_started';
                return (
                  <button
                    key={phase.key}
                    onClick={() => setActivePhase(phase.key)}
                    className={`flex-1 p-3 rounded-sm border transition-colors ${
                      activePhase === phase.key ? 'border-primary bg-muted' : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className={`w-full h-1 rounded-full mb-2 ${phaseBarCls(status, phase.color)}`} />
                    <p className="text-xs font-medium">{phase.label}</p>
                    <p className="text-xs text-muted-foreground capitalize">{status.replace('_', ' ')}</p>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Phase Details */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${phases.find(p => p.key === activePhase)?.color}`} />
                  {phases.find(p => p.key === activePhase)?.label} Phase
                </CardTitle>
                <CardDescription>{phases.find(p => p.key === activePhase)?.description}</CardDescription>
              </div>
              {canEdit && (
                <Select
                  value={project.phases?.[activePhase]?.status || 'not_started'}
                  onValueChange={(v) => handlePhaseUpdate(activePhase, { status: v })}
                  disabled={saving}
                >
                  <SelectTrigger className="w-[160px]" data-testid="phase-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_started">Not Started</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={project.phases?.[activePhase]?.notes || ''}
                  onChange={(e) => handlePhaseUpdate(activePhase, { notes: e.target.value })}
                  placeholder={`Add notes for the ${phases.find(p => p.key === activePhase)?.label} phase...`}
                  rows={5}
                  className="resize-none break-words whitespace-pre-wrap"
                  disabled={!canEdit}
                  data-testid="phase-notes"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Project Charter */}
        <Card>
          <CardHeader>
            <CardTitle>Project Charter</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-5 rounded-lg border border-blue-500/20 bg-blue-500/5">
                <h4 className="font-semibold text-sm text-blue-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  Problem Statement
                </h4>
                <p className="text-sm leading-relaxed break-words whitespace-pre-wrap">{project.problem_statement}</p>
              </div>
              <div className="p-5 rounded-lg border border-emerald-500/20 bg-emerald-500/5">
                <h4 className="font-semibold text-sm text-emerald-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  Goal
                </h4>
                <p className="text-sm leading-relaxed break-words whitespace-pre-wrap">{project.goal}</p>
              </div>
              <div className="p-5 rounded-lg border border-amber-500/20 bg-amber-500/5">
                <h4 className="font-semibold text-sm text-amber-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <Circle className="h-4 w-4" />
                  Scope
                </h4>
                <p className="text-sm leading-relaxed break-words whitespace-pre-wrap">{project.scope}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Attachments */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Paperclip className="h-5 w-5" />
                  Attachments
                </CardTitle>
                <CardDescription>Documents and files attached to this project</CardDescription>
              </div>
              <div>
                <input
                  type="file"
                  ref={attachmentInputRef}
                  onChange={handleAttachmentUpload}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt"
                />
                <Button 
                  size="sm" 
                  onClick={() => attachmentInputRef.current?.click()}
                  disabled={uploadingAttachment}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {uploadingAttachment ? 'Uploading...' : 'Add File'}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {(project.attachments?.length || 0) === 0 ? (
              <p className="text-muted-foreground text-center py-4">No attachments yet</p>
            ) : (
              <div className="space-y-2">
                {project.attachments.map(att => (
                  <div key={att.attachment_id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <FileText className="h-8 w-8 text-blue-600" />
                      <div>
                        <p className="font-medium">{att.filename}</p>
                        <p className="text-sm text-muted-foreground">
                          {att.size ? `${(att.size / 1024).toFixed(1)} KB` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {att.data && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = att.data;
                            link.download = att.filename;
                            link.click();
                          }}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500"
                        onClick={() => handleDeleteAttachment(att.attachment_id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Team Participants */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Team Participants
                </CardTitle>
                <CardDescription>People working on this project</CardDescription>
              </div>
              {isMasterAdmin && (
                <Dialog open={showAddParticipant} onOpenChange={setShowAddParticipant}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="add-participant-btn">
                      <UserPlus className="h-4 w-4 mr-2" /> Add Participant
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Team Participant</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div className="space-y-2">
                        <Label>Select User</Label>
                        <Select value={selectedUser} onValueChange={setSelectedUser}>
                          <SelectTrigger>
                            <SelectValue placeholder="Choose a user..." />
                          </SelectTrigger>
                          <SelectContent>
                            {availableUsers.length === 0 ? (
                              <SelectItem value="" disabled>No users available</SelectItem>
                            ) : (
                              availableUsers.map(u => (
                                <SelectItem key={u.user_id} value={u.user_id}>
                                  {u.name} ({u.email})
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button variant="outline" onClick={() => setShowAddParticipant(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleAddParticipant} disabled={!selectedUser || saving}>
                          Add Participant
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {(project.team_members?.length || 0) === 0 ? (
              <p className="text-muted-foreground text-center py-4">No participants added yet</p>
            ) : (
              <div className="space-y-3">
                {project.team_members.map(memberId => {
                  const memberInfo = getParticipantInfo(memberId);
                  return (
                    <div key={memberId} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-primary/10 text-primary">
                            {memberInfo.name?.charAt(0) || '?'}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{memberInfo.name || memberId}</p>
                          <p className="text-sm text-muted-foreground">{memberInfo.email}</p>
                        </div>
                      </div>
                      {isMasterAdmin && (
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleRemoveParticipant(memberId)}
                          disabled={saving}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ProjectDetailPage;
